import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Mail, Clock } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { Checkbox } from '@/components/ui/Checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { TimezoneSelect } from '@/components/ui/TimezoneSelect'
import { TimezoneOption } from './types'

interface DigestPreferencesCardProps {
  dailyEnabled: boolean
  weeklyEnabled: boolean
  sendHour: number
  timezone: string | null
  effectiveTimezone: string
  timezoneOptions: TimezoneOption[]
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${i.toString().padStart(2, '0')}:00`,
}))

export function DigestPreferencesCard({
  dailyEnabled,
  weeklyEnabled,
  sendHour,
  timezone,
  effectiveTimezone,
  timezoneOptions,
}: DigestPreferencesCardProps) {
  const [form, setForm] = useState({
    daily_digest_enabled: dailyEnabled,
    weekly_digest_enabled: weeklyEnabled,
    digest_send_hour: sendHour,
    digest_timezone: timezone ?? '',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [sampling, setSampling] = useState<'daily' | 'weekly' | null>(null)

  const handleSave = () => {
    setIsSaving(true)
    router.patch(
      '/profile/digests',
      {
        account_user: {
          ...form,
          digest_timezone: form.digest_timezone || null,
        },
      },
      {
        preserveScroll: true,
        onFinish: () => setIsSaving(false),
      },
    )
  }

  const handleSample = (kind: 'daily' | 'weekly') => {
    setSampling(kind)
    router.post(
      '/profile/digests/sample',
      { kind },
      {
        preserveScroll: true,
        onFinish: () => setSampling(null),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="size-5 text-muted-foreground" />
          <div>
            <CardTitle>Digest emails</CardTitle>
            <CardDescription>Get a snapshot of this workspace by email — daily, weekly, or both.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={form.daily_digest_enabled}
              onCheckedChange={(v) => setForm({ ...form, daily_digest_enabled: Boolean(v) })}
              className="mt-0.5"
            />
            <div className="space-y-1">
              <div className="text-sm font-medium">Daily digest</div>
              <p className="text-xs text-muted-foreground">
                Every morning: your open conversations, unassigned in the queue, what arrived overnight, what you closed.
              </p>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={form.weekly_digest_enabled}
              onCheckedChange={(v) => setForm({ ...form, weekly_digest_enabled: Boolean(v) })}
              className="mt-0.5"
            />
            <div className="space-y-1">
              <div className="text-sm font-medium">Weekly digest (Mondays)</div>
              <p className="text-xs text-muted-foreground">
                Same as daily but rolled up across the last 7 days, with trend vs the previous week and a per-day volume chart.
              </p>
            </div>
          </label>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-muted-foreground" />
              <Label htmlFor="digest_send_hour">Send time</Label>
            </div>
            <Select
              value={String(form.digest_send_hour)}
              onValueChange={(v) => setForm({ ...form, digest_send_hour: Number(v) })}
            >
              <SelectTrigger id="digest_send_hour" className="cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOUR_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="cursor-pointer">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Local time in your selected timezone.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Timezone</Label>
            <TimezoneSelect
              value={form.digest_timezone}
              options={timezoneOptions}
              onChange={(v) => setForm({ ...form, digest_timezone: v })}
              placeholder={`Inherit (${effectiveTimezone})`}
            />
            <p className="text-xs text-muted-foreground">
              {form.digest_timezone
                ? <button type="button" className="underline cursor-pointer" onClick={() => setForm({ ...form, digest_timezone: '' })}>Reset to inherit ({effectiveTimezone})</button>
                : <>Currently inheriting from your profile / account ({effectiveTimezone}).</>}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={sampling !== null}
              onClick={() => handleSample('daily')}
              className="cursor-pointer"
            >
              {sampling === 'daily' ? 'Sending…' : 'Send sample daily'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={sampling !== null}
              onClick={() => handleSample('weekly')}
              className="cursor-pointer"
            >
              {sampling === 'weekly' ? 'Sending…' : 'Send sample weekly'}
            </Button>
          </div>
          <Button type="button" onClick={handleSave} disabled={isSaving} className="cursor-pointer">
            {isSaving ? 'Saving…' : 'Save preferences'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
